import {
    ICachingList,
    SubscribeState,
    Ulid,
} from "@kase/my-marathon-communication";
import { Signal, signal } from "@preact/signals";
import { upsert, upsertOne } from "~/utils";
import {
    BaseListItemT,
    IUlidInternal,
    ListControllerParams,
} from "./interfaces";

const DEFAULT_SUBSCRIPTION_KEY = "default";
const ALL_KEY = "$$";

export const createListController = <
    ApiListItemT extends object,
    ListItemT extends BaseListItemT<ApiListItemT> = Omit<ApiListItemT, "id"> &
        BaseListItemT<ApiListItemT>,
    FilterT = void,
>({
    connection,
    list,
    getKey,
    getId = (item) => (item as { id: Readonly<Ulid> }).id.toString(),
    emit,
    processBatch,
    load,
    sort,
    getItem,
    skip,
    view = (item, filter) =>
        ({
            ...item,
            ...("id" in item
                ? { id: String(item.id), _id: item.id }
                : undefined),
            ...(getId ? { id: getId(item, filter) } : {}),
        }) as unknown as ListItemT,
}: ListControllerParams<ApiListItemT, ListItemT, FilterT>) => {
    const items = signal<ListItemT[]>([]);
    const loadingStore: Record<string, Signal<boolean>> = {
        [ALL_KEY]: signal(false),
    };
    const loadedStore: Record<string, Signal<boolean>> = {
        [ALL_KEY]: signal(false),
    };
    const loadedApiStore: Record<string, Signal<boolean>> = {
        [ALL_KEY]: signal(false),
    };
    const allLoading = loadingStore[ALL_KEY];
    const allLoaded = loadedStore[ALL_KEY];
    const subscriptions: Record<string, ICachingList<ApiListItemT>> = {};
    const cancelSubscriptions: Record<string, () => void> = {};

    const loading = (filter: FilterT) => {
        if (!getKey) {
            return allLoading;
        }

        const keyStr = getKey(filter);

        if (!loadingStore[keyStr]) {
            loadingStore[keyStr] = signal(false);
        }

        return loadingStore[keyStr];
    };

    const loadedApi = (filter: FilterT) => {
        if (!getKey) {
            return loadedApiStore[ALL_KEY];
        }

        const keyStr = getKey(filter);

        if (!loadedApiStore[keyStr]) {
            loadedApiStore[keyStr] = signal(false);
        }

        return loadedApiStore[keyStr];
    };

    const loaded = (filter: FilterT, api = false) => {
        if (api) {
            return loadedApi(filter);
        }

        if (!getKey) {
            return allLoaded;
        }

        const keyStr = getKey(filter);

        if (!loadedStore[keyStr]) {
            loadedStore[keyStr] = signal(false);
        }

        return loadedStore[keyStr];
    };

    const getSubscriptionKey = (filter: FilterT) => {
        return getKey ? getKey(filter) : DEFAULT_SUBSCRIPTION_KEY;
    };

    const getSubscription = (filter: FilterT) => {
        const key = getSubscriptionKey(filter);
        const con = connection.value;

        if (!(key in subscriptions)) {
            const sub = list(con, filter);

            subscriptions[key] = sub;
            cancelSubscriptions[key] = () => {
                sub.unsub();
                delete subscriptions[key];
                delete cancelSubscriptions[key];
                // @TODO: remove loading and loaded signals? decide when use case is clear
            };
        }

        return subscriptions[key];
    };

    const listenLoop = async (
        filter: FilterT,
        subscription: ICachingList<ApiListItemT>,
        loadingSignal: Signal<boolean>,
        loadedSignal: Signal<boolean>,
        loadedApiSignal: Signal<boolean>,
    ) => {
        loadingSignal.value = true;

        for await (const event of subscription) {
            if (event.kind === SubscribeState.Loaded) {
                loadedApiSignal.value = true;
                loadedSignal.value = true;
                loadingSignal.value = false;
            } else if (event.kind === SubscribeState.LoadedCache) {
                loadedSignal.value = true;
                loadingSignal.value = false;
            } else if (event.kind === SubscribeState.Remove) {
                const newItems = items.peek().slice();
                const removedItems: ListItemT[] = [];

                for (const value of event.values) {
                    const vid = getId(value, filter);
                    const idx = newItems.findIndex((item) => item.id === vid);

                    if (idx !== -1) {
                        removedItems.push(newItems[idx]);
                        newItems.splice(idx, 1);
                    }
                }

                items.value = newItems;
                emit?.("remove", removedItems);
            } else {
                let addedItems: ListItemT[] = [];
                const promises: Promise<ListItemT>[] = [];
                const eventValues = skip
                    ? event.values.filter((v) => !skip(v, filter))
                    : event.values;

                if (processBatch) {
                    addedItems = await processBatch(eventValues);
                } else {
                    for (const value of eventValues) {
                        const item = view(value, filter);

                        if (item instanceof Promise) {
                            promises.push(item);
                        } else {
                            addedItems.push(item);
                        }
                    }

                    if (promises.length) {
                        addedItems = await Promise.all(promises);
                    }
                }

                const newItems = upsert(items.peek().slice(), addedItems, "id");

                if (sort) {
                    newItems.sort(sort);
                }

                items.value = newItems;
                emit?.("add", addedItems);
            }
        }
    };

    const listen = (filter: FilterT) => {
        const keyStr = getSubscriptionKey(filter);
        const loadingSignal = loading(filter);
        const loadedSignal = loaded(filter);
        const loadedApiSignal = loadedApi(filter);
        const hasSubscription = keyStr in subscriptions;
        const subscription = getSubscription(filter);

        if (hasSubscription) {
            return cancelSubscriptions[keyStr];
        }

        listenLoop(
            filter,
            subscription,
            loadingSignal,
            loadedSignal,
            loadedApiSignal,
        );

        return cancelSubscriptions[keyStr];
    };

    const whenLoaded = async (filter: FilterT, api = false) => {
        const keyStr = getSubscriptionKey(filter);
        const loadingSignal = loading(filter);
        const loadedSignal = loaded(filter);
        const loadedApiSignal = loadedApi(filter);
        const hasSubscription = keyStr in subscriptions;
        const subscription = getSubscription(filter);

        if ((!api && loadedSignal.peek()) || (api && loadedApiSignal.peek())) {
            return;
        }

        if (!hasSubscription) {
            listenLoop(
                filter,
                subscription,
                loadingSignal,
                loadedSignal,
                loadedApiSignal,
            );
        }

        const signal = api ? loadedApiSignal : loadedSignal;

        return new Promise<void>((resolve) => {
            const unsub = signal.subscribe((loaded) => {
                if (loaded) {
                    resolve();
                    unsub();
                }
            });
        });
    };

    const get = (id: string | null | undefined | Readonly<Ulid>) => {
        if (!id) {
            return null;
        }

        const finalId = typeof id === "string" ? id : id.toString();

        return items.peek().find((e) => e.id === finalId) ?? null;
    };

    const getAndSub = (
        id: string | null | undefined | Readonly<Ulid> | IUlidInternal,
    ) => {
        if (!id) {
            return null;
        }

        if (
            id &&
            typeof id !== "string" &&
            "low" in id &&
            "high" in id &&
            !(id instanceof Ulid)
        ) {
            id = Ulid.fromLoHi(id).toString();
        }

        const finalId = typeof id === "string" ? id : id.toString();

        return items.value.find((e) => e.id === finalId) ?? null;
    };

    const getOrLoad = async (id: string | Readonly<Ulid>, filter: FilterT) => {
        let item = get(id);

        if (item) {
            return item;
        }

        await whenLoaded(filter);

        item = get(id);

        if (item) {
            return item;
        }

        if (!load) {
            return null;
        }

        const con = connection.value;
        const finalId = typeof id === "string" ? Ulid.fromString(id) : id;
        const promises: Promise<ListItemT>[] = [];
        let addedItems: ListItemT[] = [];

        for await (const value of load(con, finalId)) {
            const item = view(value, filter);

            if (item instanceof Promise) {
                promises.push(item);
            } else {
                addedItems.push(item);
            }
        }

        if (promises.length) {
            addedItems = await Promise.all(promises);
        }

        items.value = upsert(items.peek().slice(), addedItems, "id");

        return get(id);
    };

    const getOrForceLoad = async (
        id: string | Readonly<Ulid>,
        filter: FilterT,
    ) => {
        const item = get(id);

        if (item) {
            return item;
        }

        if (!getItem) {
            return null;
        }

        const con = connection.value;
        const finalId = typeof id === "string" ? Ulid.fromString(id) : id;
        const loadedItem = await getItem(con, finalId).current;

        if (!loadedItem) {
            return null;
        }

        items.value = upsertOne(
            items.peek().slice(),
            await view(loadedItem, filter),
            "id",
        );

        return get(id);
    };

    return {
        items,
        allLoading,
        allLoaded,
        get,
        getAndSub,
        getOrLoad,
        getOrForceLoad,
        listen,
        loading,
        loaded,
        whenLoaded,
    };
};
